import { EditOutlined, PlusOutlined, PushpinOutlined } from '@ant-design/icons';
import { Button, Card, Empty, Tag, Typography } from 'antd';
import { PERMISSIONS } from 'constants/permissions';
import { useHasAnyOfPermissions } from 'hooks/useHasPermission';
import React, { useState } from 'react';
import { useNavigate } from 'react-router';
import AddAddressDrawer from './AddAddressDrawer';
import { StyledWrapper } from './styles';

function AddressesTab({ customer, setCustomer }) {
  const navigate = useNavigate();
  const [addDrawerOpen, setAddDrawerOpen] = useState(false);
  const hasCustomerAccess = useHasAnyOfPermissions([PERMISSIONS.CUSTOMERS]);
  const addresses = customer?.customer_addresses ?? [];

  const formatAddress = (address) => {
    const parts = [
      address?.address,
      address?.city,
      address?.state,
      address?.zip_code,
    ].filter(Boolean);
    return parts.join(', ') || 'No address details';
  };

  const handleEdit = (address) => {
    navigate(`/console/customer-address/${customer.id}/${address?.id}`);
  };

  const handleAddressAdded = (newAddress) => {
    setCustomer((c) => ({
      ...c,
      customer_addresses: [...(c?.customer_addresses ?? []), newAddress],
    }));
  };

  if (!hasCustomerAccess) {
    return null;
  }

  return (
    <StyledWrapper>
      <div className="addresses-header">
        <Typography.Title level={5} className="addresses-title">
          Saved Addresses
        </Typography.Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setAddDrawerOpen(true)}
        >
          Add address
        </Button>
      </div>

      {addresses.length === 0 ? (
        <Empty
          image={<PushpinOutlined style={{ fontSize: 48, color: '#d9d9d9' }} />}
          description="No addresses on file"
          className="addresses-empty"
        >
          <Typography.Text type="secondary">
            Add an address below or when customers place orders with delivery.
          </Typography.Text>
        </Empty>
      ) : (
        <div className="addresses-grid">
          {addresses.map((address) => (
            <Card key={address?.id} className="address-card" size="small">
              <div className="address-card-header">
                <span className="address-title">{address?.title || 'Address'}</span>
                {address?.is_deleted && <Tag color="error">Deleted</Tag>}
              </div>
              <div className="address-detail">{formatAddress(address)}</div>
              {address?.details && (
                <div className="address-extra">{address.details}</div>
              )}
              {address?.delivery_instructions && (
                <div className="address-extra">
                  <Typography.Text type="secondary">Delivery: </Typography.Text>
                  {address.delivery_instructions}
                </div>
              )}
              <div className="address-actions">
                <Button
                  type="link"
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => handleEdit(address)}
                  disabled={address?.is_deleted}
                >
                  Edit address
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <AddAddressDrawer
        customerId={customer?.id}
        open={addDrawerOpen}
        onClose={() => setAddDrawerOpen(false)}
        onSuccess={handleAddressAdded}
      />
    </StyledWrapper>
  );
}

export default AddressesTab;
